import { Component, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { FormGroup, FormBuilder, Validators, FormArray, AbstractControl } from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import moment from 'moment-timezone';
import { BehaviorSubject, Observable, Subject, Subscription, catchError, combineLatest, concatMap, distinct, distinctUntilChanged, filter, firstValueFrom, from, map, of, take, takeUntil, tap, timeout } from 'rxjs';
import { DbCallError, AppService, ButtonStatus, NUMERIC_FIELD_TO_DELETE } from '../../app.service';
import { ContractService } from '../../contract/contract.service';
import { CheckTimesheetParams, DayOptions, HandleTimesheetsParams, Timesheet, TimesheetParsed, TimesheetStatus } from '../../models/timesheet';
import { TimesheetService } from '../timesheet.service';
import { NotificationService } from '../../util/notifications/notification.service';
import { CheckTs } from '../adjacent-timesheets/adjacent-timesheets.component';
import { ContractParsed } from '../../models/contract';
import { UserService } from '../../user.service';
import { NumericValidator } from '../../util/validators/validators';
import { FlexTables } from '../../data.service';
import { T7eService } from '../../t7e/t7e.service';
import { ParserService, SQL_DATE_FORMAT, SQL_TIMEZONE } from '../../parser.service';
import { ProductionService } from '../../prod/production.service';
import { ShootingLocation } from '../../models/location';
import { config, IS_DENNIS } from '../../config';
import { FeeFields } from './ts-editor-readonly/ts-editor-readonly.component';
import { Dispo, ShootingDay } from '../../models/dispo';
import { PrecalcPopupData } from './precalc-popup/precalc-popup.component';
import { TsCalcInfo } from '../../models/ts-calc-info';
import { MatDialog } from '@angular/material/dialog';
import { PromptOtherAllowancePopupComponent } from '../prompt-other-allowance-popup/prompt-other-allowance-popup.component';

@Component({
  selector: 'app-edit-timesheet',
  templateUrl: './edit-timesheet.component.html',
  styleUrls: ['./edit-timesheet.component.scss']
})
export class EditTimesheetComponent implements OnInit, OnDestroy {
  readonly DISPLAY_OVERRIDE_FIELDS = false
  readonly MEAL_PENALTY = config.HU.TS.MEAL_PENALTY
  readonly IS_DENNIS = IS_DENNIS;
  readonly MEAL_PENALTY_15_MIN = config.HU.TS.MEAL_PENALTY_15_MIN
  readonly ALLOW_PERDIEM = config.ALLOW_PERDIEM
  readonly ALLOW_TRAVELDAY = config.ALLOW_TRAVELDAY
  readonly ALLOW_REST_DAY = config.ALLOW_REST_DAY
  readonly ALLOW_WORK_ON_REST_DAY = config.ALLOW_WORK_ON_REST_DAY
  readonly MULTIPLE_CURRENCIES = config.MULTIPLE_CURRENCIES
  readonly HIDE_KM_FEE = config.HU.TS.HIDE_KM_FEE

  readonly _destroy$ = new Subject<void>()

  private readonly _ts$ = new BehaviorSubject<TimesheetParsed | null>(null)
  refetchTimeoutHandler: NodeJS.Timeout;
  private get _ts() { return this._ts$.value }
  get codeOther() { return this._ts?.f_code_other ?? this.currContract$.value?.code_other }

  @Input()
  set timesheet(value: TimesheetParsed | null) {
    // for (let i = 0; i < this.adjacentTsParams$.length; i++) {
    //   this.removeAdjacentCheck(i)
    // }

    this._ts$.next(value)
  }
  
  private _dates: Date[] = []
  @Input() 
  set dates(value: Date[] | null | undefined) {
    if (value) {
      this._dates = value
      this.setDates(value)
    } else {
      this._dates = []
      this.setDates([new Date()])
    }
  }
  get dates(): Date[] | null | undefined {
    return this._dates
  }

  @Input() layout: 'grid-form' | 'vertical-form' = 'vertical-form'
  @Input() showContract: boolean = true
  @Input() showStatus: boolean = true
  @Input() canAddDate: boolean = true
  @Output() selectedDateChange = new EventEmitter<Date[]>()
  @Output() timesheetChange = new EventEmitter<TimesheetParsed | null>()
  
  readonly AUTOFILL_TIMES = false

  get isOwnTs() { return !this._ts?.usid ? true : this._ts?.usid === this.userSvc?.loggedinUser$.value?.usid }
  get isDebug() { return this.appSvc.debug}
     
  readonly form: FormGroup = this.getFG({ dStartDate: new Date(), dEndDate: new Date() }) // csak azért, hogy ne nyüszítsen a TS, hogy nincs form

  isLoading: boolean = false
  isSavingError$ = new BehaviorSubject<DbCallError>(null)
  isSavingError: DbCallError = false
  isSaving: boolean = false
  showPrecalcPopup = false

  showTooManyConseqWorkdaysPopup = false
  /** Ezzel lehet await-tel megvárni a felhasználó választását a showTooManyConseqWorkdaysPopup-ban */
  resolveTooManyConseqWorkdaysPopup: ((result: boolean) => void) | null = null

  isPrecalcLoading$ = new BehaviorSubject<boolean>(false)

  trackByIndex = this.appSvc.trackByIndex
  getUserById = this.userSvc.getUserById
  userContracts$ = this.contractSvc.userContracts$
  currContract$ = new BehaviorSubject<ContractParsed | null>(null)
  adjacentTsParams$: BehaviorSubject<HandleTimesheetsParams | null>[] = []
  aOverlapse$: BehaviorSubject<CheckTs | null>[] = []
  aOverlapseSubscriptioins: Subscription[] = []
  locations$ = new BehaviorSubject<ShootingLocation[] | null>(null)
  mealPenaltyRate: number | null = null;
  history?: { tableid: number, id: number, propcode?: string, colname?: string}
  showHistoryButtons: boolean = false
  tsIdForPrevTs: number | null = null
  warnSalaryOverride$ = new BehaviorSubject<('halfDay' | 'travelDay' | 'restDay')[]>([])
  isSavedAndCalcSalaryEqual = this.tsSvc.isSavedAndCalcSalaryEqual
  isSavedAndCalcOtEqual = this.tsSvc.isSavedAndCalcOtEqual
  isSavedAndCalcTaEqual = this.tsSvc.isSavedAndCalcTaEqual
  exchangeRatesLoading$ = this.prodSvc.currencyExchangeRatesLoading$
  exchangeRatesError$ = this.prodSvc.currencyExchangeRatesError$
  exchangeRates$ = this.prodSvc.currencyExchangeRates$
  isExchangeRateReady$ = new BehaviorSubject<boolean>(false)
  showSaveComment: { title: string, saveButtonText: string, isCommentOptional: boolean, newStatus: TimesheetStatus } | null = null
  selectedReadOnlyFields: FeeFields = 'calc'
  workhourValueChangeSubscription?: Subscription
  warnWorkhourChangeHasNoEffect$ = new BehaviorSubject<boolean>(false)
  allowPerdiem$ = this.currContract$.pipe(map(c =>
    (this.ALLOW_PERDIEM === true || (this.ALLOW_PERDIEM === 'onlyForMoreThanDeptHead' && this.isMoreThanDeptHead))
    && (c?.f_saved_sleepoverrate || c?.f_sleepoverrate)))
  shootingDays$ = new BehaviorSubject<ShootingDay_ForPage[]>([])
  
  minDate$ = new BehaviorSubject<Date | moment.Moment | null | undefined>(null)
  maxDate: Date | null = moment().endOf('day').toDate()

  constructor(
    private tsSvc: TimesheetService,
    private notifSvc: NotificationService,
    private appSvc: AppService,
    private contractSvc: ContractService,
    private userSvc: UserService,
    private prodSvc: ProductionService,
    private fb: FormBuilder,
    private route: ActivatedRoute,
    private t7e: T7eService,
    private parser: ParserService,
    private dialog: MatDialog,
  ) {
    this.appSvc.pageTitle$.next(this.t7ePageTitle)
    this.setDates = this.setDates.bind(this)
    this.scrollFormIntoView = this.scrollFormIntoView.bind(this)
    this.warnHolidayIfNeeded = this.warnHolidayIfNeeded.bind(this)

    this.tsSvc.clearLastInvoicedTsCache()
    this.prodSvc.fetchCurrencyExchangeRates()
    this.prodSvc.fetchDays()
    this.refetchTimeoutHandler = setTimeout(this.prodSvc.fetchDays.bind(this.prodSvc), 60000)

  combineLatest([this.currContract$, this.prodSvc.prodDefaults$])
    .pipe(
      map(([contract, prodDefaults]) => {
        return contract?.dStartdate ?? prodDefaults?.dShootingstart
      })
    )
    .subscribe(this.minDate$)
    
    combineLatest([this.currContract$, this.exchangeRatesLoading$, this.exchangeRatesError$])
      .pipe(
        takeUntil(this._destroy$),
        map(([contract, exchangeRatesLoading, exchangeRatesError]) => {
          // console.log('retVal, f_currency, exchangeRatesLoading, exchangeRatesError', contract?.f_currency === this.prodSvc.getCurrency()
          //   || (!exchangeRatesLoading && exchangeRatesError === false), contract?.f_currency, exchangeRatesLoading, exchangeRatesError)
          return contract?.f_currency === this.prodSvc.getCurrency()
            || (!exchangeRatesLoading && exchangeRatesError === false)
        })
      )
      .subscribe(this.isExchangeRateReady$)

    // csak azért, hogy ne nyüszítsen a TS, hogy nincs form
    this.form.setValue(this.getFG({ dStartDate: new Date(), dEndDate: new Date() }).getRawValue())

    this.isSavingError$
      .pipe(takeUntil(this._destroy$))
      .subscribe(x => this.isSavingError = x)

    this._ts$
      .pipe(takeUntil(this._destroy$))
      .subscribe({
      next: (ts) => {
        console.log('ts', ts?.tsid, ts?.depname, ts?.usname, ts?.dStartDate)
        this.selectedReadOnlyFields = this.tsSvc.isApprovedOrHigher(ts) ? 'saved' : 'calc'
        let tempFG: FormGroup
        if (ts?.tsid) {
          // call removeDate until there is only one date
          while (this.datesFA?.length > 1) {
            this.removeDate(1)
          }
          tempFG = this.getFG(ts)
          this.currContract$.next(this.contractSvc.getContractById(ts.conid!))
          this.onKmOrCommentChange(0)
          this.setLocation(0, ts)
        } else { 
          tempFG = this.getFG(ts)
          this.setDates(this._dates)
          this.currContract$.next(this.contractSvc.selectedContract)
        }
        this.form.setValue(tempFG.getRawValue())

        this.workhourValueChangeSubscription?.unsubscribe()
        this.workhourValueChangeSubscription = this.form.get('workhours')?.valueChanges
          .pipe(
            takeUntil(this._destroy$),
            filter(_ => !!ts?.tsid),
            distinctUntilChanged(),
          )
          .subscribe(this.onWorkhoursChange.bind(this))

        if (!this.canEdit(ts)) {
          // valamiért setTimeout() nélkül csak a workhours és a to és from time lesz disabled
          setTimeout(() => { this.form.disable() }, 0)
        } else {
          this.form.enable()
          if (!this.userSvc.isMoreThanDeptHead) {
            this.form.get('salaryOverride')?.disable()
          }
        }
          
        if (ts) this.mealPenaltyRate = this.contractSvc.getFirstValuableOtRateByContract(this.currContract$.value)
          
        this.form.markAsPristine()
        this.checkTs()
      },
    })
  
    this.currContract$
      .pipe(takeUntil(this._destroy$))
      .subscribe(this.scrollFormIntoView)
    
    this.prodSvc.locations$.pipe(
      takeUntil(this._destroy$),
      map(x => x
        ?.sort((a, b) => a.locName.localeCompare(b.locName))
        .filter(x => x.enabled)
        || []
      )
    ).subscribe(this.locations$)

    // ha változik a kiválasztott nap, számolja újra a crew callokat
    combineLatest([this.form.valueChanges, this.prodSvc.days$, this.prodSvc.locations$])
      .pipe(takeUntil(this._destroy$))
      .subscribe(([timesheets, prodDays, locations]) => {
        //console.log('dates', timesheets, 'days', prodDays)
        const shDays = parser.disposToShootingDays(prodDays, locations || [])
        const crewCalls: ShootingDay_ForPage[] = timesheets.dates
          .map((ts: any) => this.getShootingDayProps(ts.fromDate, shDays))
        this.shootingDays$.next(crewCalls)
      })

    if (!this.locations$.value?.length) this.prodSvc.fetchShootingLocations()
  }

  /**
   * Get crew call and camera wrap for a given timesheet date
   * @param tsDate Kiválasztott nap
   * @param prodDays Forgatási napok (Dispo)
   * @returns 
   */
  private getShootingDayProps = (tsDate: string, prodDays: ShootingDay[]): ShootingDay_ForPage => {
    if (!tsDate) return { cc: null, cw: null, taHoursOverride: null }
    const prodDay = prodDays.find(d => moment(d.crewCallTime).isSame(tsDate, 'day'))
    return {
      cc: prodDay && prodDay.crewCallTime ? moment(prodDay.crewCallTime) : null,
      cw: prodDay && prodDay.cameraWrapTime ? moment(prodDay.cameraWrapTime) : null,
      taHoursOverride: prodDay ? prodDay.taHoursOverride ?? null : null,
    }
  }

  onLocationChange(dateIndex: number, locCode: string): void {
    if (locCode && !this.HIDE_KM_FEE && !confirm(this.t7eLocationConfirmEligibleText)) {
      this.getDateFG(dateIndex).get('location')?.setValue('')
      return
    }
    const location: ShootingLocation | null = !locCode
      ? null
      : this.prodSvc.locations$.value?.find(l => l.locCode == locCode) || null
    const kmFee = this.convertIfNecessary(location?.kmFee || 0, this.getDateFG(dateIndex).value.fromDate)
    this.getDateFG(dateIndex).get('kmFee')?.setValue(kmFee) || 0
    this.getDateFG(dateIndex).get('kmComment')?.setValue(location?.locName) || ''
    this.disableKmAndComment(dateIndex, locCode)
  }

  private disableKmAndComment(dateIndex: number, locCode: string): boolean {
    if (!!locCode?.trim()) {
      this.getDateFG(dateIndex).get('kmFee')?.disable()
      this.getDateFG(dateIndex).get('kmComment')?.disable()
      return true
    }
    else {
      this.getDateFG(dateIndex).get('kmFee')?.enable()
      this.getDateFG(dateIndex).get('kmComment')?.enable()
      return false
    }
  }

  onKmOrCommentChange(dateIndex: number): void {
    if (this.HIDE_KM_FEE) return
    if (this.getDateFG(dateIndex)?.get('location')?.getRawValue()?.trim()) return
    const kmFee = this.getDateFG(dateIndex).get('kmFee')?.getRawValue()?.toString().trim()
    const isKmFeeZero = parseInt(kmFee) === 0
    const kmComment = this.getDateFG(dateIndex).get('kmComment')?.getRawValue() ?.trim()
    if ((!!kmFee && !isKmFeeZero) || !!kmComment) {
      this.getDateFG(dateIndex)?.get('location')?.disable()
    } else {
      this.getDateFG(dateIndex)?.get('location')?.enable()
    }
  }

  setLocation(dateIndex: number, ts: TimesheetParsed): void {
    this.getDateFG(0)?.get('location')?.setValue(ts.f_location)
  }

  private convertIfNecessary(fee: number | null, date: Date): number | null {
    if (!this.MULTIPLE_CURRENCIES || this.currContract$.value?.f_currency === this.prodSvc.getCurrency()) return fee
    if (!fee) return 0
    const showErrMsg = () => {
      const msg = this.t7e.lang === 'hu'
        ? `A ${this.currContract$.value?.f_currency} árfolyam nem áll rendelkezésre. Töltsd ki kézzel a km díjat`
        : `The  ${this.currContract$.value?.f_currency} is not available. Fill the km fee manually`
      this.notifSvc.alert(msg, this.currContract$.value?.f_currency || '')
    }
    if (!this.isExchangeRateReady$.value) {
      showErrMsg()
      return null
    }
    try {
      const rate = this.MULTIPLE_CURRENCIES === 'fixed-rates'
        ? this.prodSvc.getExchangeRateForFixedRates(this.currContract$.value?.f_currency)
        : this.prodSvc.getCurrencyExchangeRateByDate(date)      
      return Math.round(fee * 100 / rate) / 100
    } catch (error) {
      console.error('Error while converting currency:', error)
      showErrMsg()
      return null
    }
  }

  private onWorkhoursChange(workhours: number | null): void {
    if (!this._ts) return
    if (this.tsSvc.isApprovedOrHigher(this.ts) && this.ts?.workhours != workhours) {
      this.warnWorkhourChangeHasNoEffect$.next(true)
      this.notifSvc.alert(this.t7eNotifChangingWorkhoursHasNoEffect, this.t7eNotifChangingWorkhoursHasNoEffectTitle)
    } else {
      this.warnWorkhourChangeHasNoEffect$.next(false)
    }
    this.checkTs()
    if (this.AUTOFILL_TIMES) {
      const tsWithNewTimes = this.setDatesIfNew(this.form.value)
      if (tsWithNewTimes) {
        this.form.get('fromTime')?.setValue(moment(tsWithNewTimes?.dStartDate).format('HH:mm'))
        this.form.get('toTime')?.setValue(moment(tsWithNewTimes?.dEndDate).format('HH:mm'))
      }
    } else {
      this.focusFromTime()
    }
    this.scrollFormIntoView()
  }

  onWarnhourChangeHasNoEffectClicked(): void {
    if (this.warnWorkhourChangeHasNoEffect$.value) {
      this.notifSvc.alert(this.t7eNotifChangingWorkhoursHasNoEffect, this.t7eNotifChangingWorkhoursHasNoEffectTitle)
    }
  }

  private focusFromTime() {
    setTimeout(() => {
      const fromTime = document.getElementById('fromTime') as HTMLInputElement
      if (fromTime) {
        fromTime.focus()
        fromTime.select()
      }
    }, 0)
  }

  get ts() { return this._ts }
  get isEdit() { return !!this._ts?.tsid }
  get datesFA() { return this.form.get('dates') as FormArray }
  get isAtCrewMember() { return !this._ts?.tsid ||  this._ts$?.value?.tsstatus == TimesheetStatus.atCrewMember }
  get isDeleted() { return this._ts$?.value?.tsstatus == TimesheetStatus.deleted }
  get isDisabled() { return this._ts$?.value?.tsstatus == TimesheetStatus.disabled }
  get statusClass() {
    return this.tsSvc.getStatusClass(this.ts)
  }
  get statusName() {
    return this.tsSvc.getStatusName(this.ts?.tsstatus)
  }
  get statusSaveComment() {
    return this.ts?.savecomment
  }
  get statusOf() { return this._ts?.tsid ? {tableId: FlexTables.timesheets, id: this._ts?.tsid} : undefined }
  get showContractSection() {
    return this.showContract && (
      this.userContracts$.value?.length != 1
      // nem egy törölt szerződéshez tartozik?
      || this._ts$?.value?.conid == this.userContracts$.value?.[0]?.conid
    )
  }
  get tsWorkhours() { return this.form?.getRawValue().workhours }
  get isTimeSet() {
    if  (this._ts?.tsid) return true
    if (this.form?.get('fromTime')?.dirty) return true
    if (this.form?.get('toTime')?.dirty) return true
    return false
  }
  get isWaitingForProdMgr() {
    const statuses: (TimesheetStatus | null | undefined)[] = [
      TimesheetStatus.atProdMgr,
      TimesheetStatus.deptHeadApproved,
    ]
    return statuses.includes(this._ts?.tsstatus)
  }
  get isApprovedOrHigher() { return this.tsSvc.isApprovedOrHigher(this._ts) }
  get rentalName1() { return this.currContract$.value?.f_dailybasedrentalrate1name }
  get rentalFee1() { return this.currContract$.value?.f_dailybasedrentalrate1 }
  get rentalName2() { return this.currContract$.value?.f_dailybasedrentalrate2name }
  get rentalFee2() { return this.currContract$.value?.f_dailybasedrentalrate2 }
  get rentalName3() { return this.currContract$.value?.f_dailybasedrentalrate3name }
  get rentalFee3() { return this.currContract$.value?.f_dailybasedrentalrate3 }
  get rentalName4() { return this.currContract$.value?.f_dailybasedrentalrate4name }
  get rentalFee4() { return this.currContract$.value?.f_dailybasedrentalrate4 }
  get rentalName5() { return this.currContract$.value?.f_dailybasedrentalrate5name }
  get rentalFee5() { return this.currContract$.value?.f_dailybasedrentalrate5 }
  get rentalName6() { return this.currContract$.value?.f_dailybasedrentalrate6name }
  get rentalFee6() { return this.currContract$.value?.f_dailybasedrentalrate6 }
  get hasMonthlyRents() {
    return !!(this.currContract$.value?.f_monthlybasedrentalrate1
      || this.currContract$.value?.f_monthlybasedrentalrate2
      || this.currContract$.value?.f_monthlybasedrentalrate3)
  }
  get contractHasMealPenalty() { return this.contractSvc.hasMealPenalty_setAtProdDeptSf(this.currContract$.value) }
  contractMealPenaltyRate = this.contractSvc.getMealPenaltyRate_setAtProdDeptSf

  isProdMgr = this.userSvc.isProdMgr
  isFinance = this.userSvc.isFinance
  isDeptHead = this.userSvc.isDeptHead
  isOnlyDeptAdmin = this.userSvc.isOnlyDeptAdmin
  isMoreThanCrew = this.userSvc.isMoreThanCrew
  isMoreThanDeptHead = this.userSvc.isMoreThanDeptHead
  isDev = this.userSvc.isDev
  canEdit = this.tsSvc.canEdit
  canApprove = this.tsSvc.canApprove
  canSendBack = this.tsSvc.canSendBackToCrew
  canGenerateAtt = this.tsSvc.canGenerateAtt
  canSetAttSent = this.tsSvc.canSetPdfSent
  canSetInvAccepted = this.tsSvc.canSetInvoiceAccepted
  
  lang = this.t7e.lang
  get hideWhenInvoiceContact() { return this.contractSvc.hideWhenInvoiceContact(this.currContract$.value) }
  get currency() { return this.currContract$.value ? this.contractSvc.getCurrency(this.currContract$.value) : ''}

  canDeptHeadApprove(ts: Timesheet | null): ButtonStatus {
    if (!this._ts?.conid) return { disabled: true, title: this.t7eBtnSubmitNoContractTitle }
    if (!this.form.valid) return { disabled: true, title: this.t7eBtnSubmitFormInvalidTitle }
    if (this._ts.tsid && this.form.dirty) return { disabled: true, title: this.t7eBtnSubmitFormNotSavedTitle }
    if (this.isSaving) return { disabled: true, title: this.t7eBtnSubmitSavingTitle }
    return this.tsSvc.canDeptHeadApprove(ts)
  }

  canDelete(ts: Timesheet | null): ButtonStatus {
    if (this._ts?.tsid && this.form.dirty) return { disabled: true, title: this.t7eBtnSubmitFormNotSavedTitle }
    if (this.isSaving) return { disabled: true, title: this.t7eBtnSubmitSavingTitle }
    return this.tsSvc.canDelete(ts)
  }

  get canSubmit(): ButtonStatus {
    if (!this._ts?.conid) return { disabled: true, title: this.t7eBtnSubmitNoContractTitle }
    if (!this.form.valid) return { disabled: true, title: this.t7eBtnSubmitFormInvalidTitle }
    if (this._ts.tsid && this.form.dirty) return { disabled: true, title: this.t7eBtnSubmitFormNotSavedTitle }
    if (this.isSaving) return { disabled: true, title: this.t7eBtnSubmitSavingTitle }
    if (!this.isAdjacentCheckDone$.value) return { disabled: true, title: this.t7eBtnSubmitAdjacentCheckNotDoneTitle }
    if (this.aOverlapse$
      .some(x => (x?.value?.sametsid != this._ts?.tsid || this._ts?.tsid === null)
        && x.value?.overlaptsid)) return { disabled: true, title: this.t7eBtnSubmitOverlapTitle }
    if (this.aOverlapse$
      .some((checkTsResult$, dateIndex) => !checkTsResult$?.value || checkTsResult$?.value.isAleadyInvoicedPeriod)) {
      return { disabled: true, title: this.t7eNotifAlreadyInvoicedWarning }
    }
    if (this.userSvc.isProdMgr) {
      switch (this._ts.tsstatus) {
        case TimesheetStatus.invAccepted:
          return { disabled: true, title: this.t7eBtnSubmitInvAcceptedTitle };
        case TimesheetStatus.attGenerating:
          return { disabled: true, title: this.t7eBtnSubmitAttGeneratingTitle };
      }
    } else if (this.userSvc.isDeptHead) {
      if (this.isDateFuture()) return { disabled: true, title: this.t7eBtnSubmitFutureTitle }
      if (this._ts.tsid && this._ts.tsstatus != TimesheetStatus.atDeptHead) {
        if (this._ts.tsid && this._ts.tsstatus != TimesheetStatus.atCrewMember) {
          return { disabled: true, title: this.t7eBtnSubmitAlreadySubmittedTitle }
        } else {
          return { disabled: false, title: '' } 
        }
      }
    } else if (this.userSvc.isCrew) {
      // jövőbeni ts-t nem lehet beküldeni
      if (this.isDateFuture()) return { disabled: true, title: this.t7eBtnSubmitFutureTitle }
      if (this._ts.tsid && this._ts.tsstatus != TimesheetStatus.atCrewMember) {
        return { disabled: true, title: this.t7eBtnSubmitAlreadySubmittedTitle }
      }
    } else {
      return { disabled: true, title: this.t7eBtnSubmitNoPermissionTitle }
    }
      
    return { disabled: false, title: '' }
  }

  private isDateFuture(): boolean {
    const dEndDate = this.form.getRawValue().dates.reduce((acc: Date, curr: any, i: number) => {
      let { enddate } = this.getTimes(i)
      return moment(enddate).isAfter(moment(acc)) ? enddate : acc
    }, new Date(0))
    return moment(dEndDate).isAfter(moment())
  }
  
  async ngOnInit(): Promise<void> {
    // Ha nincs @Input() timesheet, akkor próbáljuk meg betölteni a route-ból
    if (!this._ts) {
      const routeParams = this.route.snapshot.paramMap
      const tsidFromRoute = Number(routeParams.get('tsid')) || null
      if (!tsidFromRoute) {
        console.error('ts null');
        return
      }
      try {
        this.isLoading = true
        const tsFromRoute = await firstValueFrom(this.tsSvc.listTs({ _tsid: tsidFromRoute }))
        if (!tsFromRoute?.length) {
          console.error('Ismeretlen ts (route-ból): ' + tsidFromRoute);
          this.notifSvc.addObservableNotif({ msg: this.t7eNotifUnknownTs, class: 'danger' })
          return
        }
        if (tsFromRoute.length !== 1) {
          console.error('Több ts ugyanazzal a tsid-val (route-ból): ' + tsidFromRoute);
          this.notifSvc.addObservableNotif({ msg: this.t7eNotifMultipleTsFromRouteError, class: 'danger' })
          return
        }
        this.timesheet = tsFromRoute[0] && this.tsSvc.parseTs(tsFromRoute[0])
        if (!this.isOwnTs) this.showContract = true
        this.currContract$.next(this.contractSvc.getContractById(tsFromRoute[0].conid))
      } catch (err) {
        console.error(err);
        this.notifSvc.addObservableNotif({ msg: this.t7eNotifTsLoadError, class: 'danger' })
      } finally {
        this.isLoading = false
      }
    }
    // Az @Input() timesheet vagy a route-ból betöltött timesheet
    /// ez kell??  if (this._dates.length) this.setDates(this._dates)

  }

  ngOnDestroy(): void {
    this._destroy$.next()
    this._destroy$.complete()
    this.aOverlapseSubscriptioins.forEach(x => x.unsubscribe())
    clearTimeout(this.refetchTimeoutHandler)
  }

  getDateFG(dateIndex: number): FormGroup {
    return this.datesFA.controls[dateIndex] as FormGroup
  }

  hasPerdiem(dateIndex: number): boolean {
    return !!this.getDateFG(dateIndex).get('perdiem')?.value
  }

  hasTravelday(dateIndex: number): boolean {
    return !!this.getDateFG(dateIndex).get('travelDay')?.value
  }

  hasRestday(dateIndex: number): boolean {
    return !!this.getDateFG(dateIndex).get('restDay')?.value
  }

  /** Be van-e pipálva az i-edik dateFG-n, hogy dolgozhat pihenőnapon */
  isWorkOnRestDay(dateIndex: number): boolean {
    return !!this.getDateFG(dateIndex).get('workOnRestDay')?.value
  }

  get canSave(): ButtonStatus {
    if (this.isDebug) return { disabled: false, title: this.t7eBtnSaveDebugTitle }
    if (this.isSaving) return { disabled: true, title: this.t7eBtnSaveSavingTitle }
    if (!this.form.dirty) return { disabled: true, title: this.t7eBtnSaveNoChangeTitle }
    if (!this.form.valid) return { disabled: true, title: this.t7eBtnSaveFormInvalidTitle }
    else return { disabled: false, title: this.t7eBtnSaveTitle }
  }

    /** Ha új TS-t szeretnénk létrehozni, akkor a dStartDate-t és dEndDate-t állítsuk be */
  private setDatesIfNew(ts: TimesheetParsed | null): TimesheetParsed | null {
    if (this.isTimeSet) return ts
    if (ts?.tsid) return ts
    if (this.AUTOFILL_TIMES) return ts
    const wh = this.tsWorkhours || this.contractSvc.getDayLen()
    if (!wh) return ts
    
    ts!.dStartDate = moment().subtract(wh, 'hours').toDate()
    ts!.dEndDate = moment().toDate()
    
    return ts

  }

  private save(params: HandleTimesheetsParams[],
    successText: string | null, errorText: string | null,
    hNotifSave?: number | null,
    successHandler?: (timesheets: TimesheetParsed[]) => any,
    errorHandler?: (err: any) => void): number {
    
    this.isSaving = true
    this.isSavingError$.next(null)
    hNotifSave = this.appSvc.isNumber(hNotifSave)
      ? hNotifSave 
      : this.notifSvc.addObservableNotif({ msg: this.t7eNotifSubmitting, duration: false, class: 'info' })
    this.tsSvc.handleMultipleTs(params)
      .pipe(takeUntil(this._destroy$))
      .subscribe({
        next: response => {
          this.isSaving = false
          this.isSavingError$.next(false)
          if (response?.length !== params.length) {
            this.notifSvc.modifyNotif(hNotifSave!, { msg: 'Adatbázis hiba. Talán nem sikerült a mentés', class: 'danger' })
            this.tsSvc.listTs({})
          } else {
            successText && this.notifSvc.modifyNotif(hNotifSave!, { msg: successText, class: 'success', duration: 2000 })
            this.timesheet = response[0]
            successHandler && successHandler(response)
          }
        },
        error: err => {
          const parsedErr = this.parser.parseSqlErrMsg(err)
          this.isSaving = false
          errorText && this.notifSvc.modifyNotif(hNotifSave!, { msg: errorText + ' - ' + parsedErr, class: 'danger' })
          this.isSavingError$.next(err)
          errorHandler && errorHandler(err)
        }
      })
    return hNotifSave!
  }

  onSaveClicked(): void {
    const aErr: string[] = []
    for (let i = 0; i < this.datesFA.length; i++) {
      const params = this.getAllSaveParams(i, aErr)
      if (!params) {
        this.notifSvc.addObservableNotif({msg: this.t7eNotifCannotSaveError + aErr.join("\r\n"), class: 'danger'})
        return
      }
      this.save([params], this.t7eNotifSaveSuccess, this.t7eNotifSaveError)
    }

  }

  /**
   * 
   * @param submitAsDisabled Force submit as disabled without notifications
   */
  async onSubmit(submitAsDisabled: boolean = false, forceStatus?: TimesheetStatus): Promise<void> {
    const aErr: string[] = []
    const aParams: HandleTimesheetsParams[] = []
    for (let i = 0; i < this.datesFA.length; i++) {
      let precalcFees_Cache: TsCalcInfo | null = null
      const err = !submitAsDisabled && await this.isTooOldForOt(i, precalcFees_Cache)
      if (err) {
        const { startdate } = this.getTimes(i)
        this.notifSvc.confirm({
          message: this.t7eCheckAgeTooOldNoOt,
          title: startdate.local().format('MMM-DD') + ': ' + this.t7eCheckAgeTooOldTitle,
          okButtonText: this.t7eSubmitAsDisabled, cancelButtonText: this.t7eModifyTimes,
          noButtonText: null,
          okCallback: async () => { await this.onSubmit(true) },
          cancelCallback: () => {  },
        })
        continue
      }
      const params = this.getAllSaveParams(i, aErr)
      if (!params) {
        this.notifSvc.addObservableNotif({ msg: i + 1 + this.t7eNotifNthTsCannotSaveError + aErr.join("\r\n") })
        continue
      }
      if (this.ts?.tsid) params._tsstatus = this.tsSvc.hasDeptHead(this.ts)
          ? TimesheetStatus.atDeptHead
          : TimesheetStatus.atProdMgr
      else if (submitAsDisabled || this.isOld(i)) {
        params._tsstatus = TimesheetStatus.disabled
        params._savecomment = this.t7eCheckAgeTooOldTitle
        if (!submitAsDisabled) this.notifSvc.alert(this.t7eCheckAgeTooOld, this.t7eCheckAgeTooOldTitle)
      }
      else if (forceStatus !== undefined) {
        params._tsstatus = forceStatus
      }

      if (params) aParams.push(params)
      await this.fillCalcFeesForDateIndex(i, params, precalcFees_Cache)
    }

    if (!aParams.length) return // mind régi OT-vel

    // 7. egybefüggő napokra figyelmeztető popup
    const aTooManyConseqDays = this.precalculatedFees$.value.map(x => this.tsSvc.isConsideredTooManyConseqDays(x))
    if (aTooManyConseqDays.some((x, i) => x && !this.isWorkOnRestDay(i))) {
      const confirmed = await this.toggleTooManyConseqWorkdaysPopup()
      if (!confirmed) return
      aTooManyConseqDays.forEach((isTooMany, index) => {
        this.getDateFG(index).get('workOnRestDay')?.setValue(true)
      })
      for (let i = 0; i < aParams.length; i++) {
        const params = aParams[i]
        const paramData = JSON.parse(params._data ?? '{}')
        paramData.longresthours = this.currContract$.value?.f_shortresthours ?? 11
        params._data = JSON.stringify(paramData)

        await this.fillCalcFeesForDateIndex(i, params, null)
      }
    }

    //
    if (config.DISPLAY_CALC_FEES_BEFORE_SUBMIT_TS
      && aParams.every(param => !param._tsstatus || param._tsstatus === TimesheetStatus.atProdMgr)
      && this.precalculatedFees$.value.some(x => !this.tsSvc.areFeesDefault(x))
    ) {
      // submit to Prod Mgr
      this.showPrecalcPopup = true
    } else {
      this.save(aParams, this.t7eNotifSubmitSuccess, this.t7eNotifSaveErrorColon)
    }
  }

  
  private async toggleTooManyConseqWorkdaysPopup(): Promise<boolean> {
    this.showTooManyConseqWorkdaysPopup = true
    return new Promise<boolean>((resolve) => {
      this.resolveTooManyConseqWorkdaysPopup = (result: boolean) => {
        this.showTooManyConseqWorkdaysPopup = false
        resolve(result)
      }
    })
  }
  test() {
    alert('test')
  }
  onTooManyConseqWorkdaysAction(confirmed: any): void {
    if (this.resolveTooManyConseqWorkdaysPopup) {
      this.resolveTooManyConseqWorkdaysPopup(confirmed)
      this.resolveTooManyConseqWorkdaysPopup = null
    }
  }

  precalcFeesConfirmed(): void {
    this.showPrecalcPopup = false

    // look for inconsistencies
    if (this.precalculatedFees$.value.some(precalc => !precalc.saveParams)) {
      console.error('Inconsistency in precalculated fees saveParams. Some saveParams are missing.')
      console.log('precalc', this.precalculatedFees$.value)
      return
    }

    const aParams: HandleTimesheetsParams[] = this.precalculatedFees$.value
      .map((precalc) => precalc.saveParams!)
    this.save(aParams, this.t7eNotifSubmitSuccess, this.t7eNotifSaveErrorColon)
  }


  private isOld(i: number): boolean {
    if (!config.DISABLE_OLD_TS_PREP_WEEKS && !config.DISABLE_OLD_TS_SHOOTING_WEEKS) return false
    if (this.isProdMgr) return false
    if (this.ts?.tsid) return false
    // only check for new TS
    const { startdate } = this.getTimes(i)
    // get the monday of the week before last week
    // on monday, they get an extra week
    const weeksToSubtract =
      ((this.prodSvc.isPrep
        ? (config.DISABLE_OLD_TS_PREP_WEEKS || 999)
        : (config.DISABLE_OLD_TS_SHOOTING_WEEKS || 999))
      + (this.isTodayMonday ? 1 : 0))
    const lastMonday = moment().subtract(weeksToSubtract, 'weeks').startOf('isoWeek')
    if (startdate.isBefore(lastMonday)) return true
    return false
  }
  
  /**
   * Checks if the TS is too old for OT
   * @param i nth date
   * @param precalcFees_Cache optional output parameter to get the precalculated fees for the date 
   * to be used outside of this function. This is to cache the result of the DB call
   * null: means that the cache is empty and the function should fill it
   * defined CalculatedFeesForDay: means that the cache is filled and the function should use it
   * undefined: means that the cache should not be used
   * @returns if ProdMgr: false. if Prep: false, else if ts is for before the day before yesterday, and the SQL thinks it would get OT>0. In case the db call fails, it return false
   */
  private async isTooOldForOt(i: number, precalcFees_Cache?: TsCalcInfo | null): Promise<boolean> {
    if (!config.DISABLE_OLD_TS_OT) return false
    if (this.isProdMgr) return false
    if (this.prodSvc.isPrep) return false
    const { startdate, enddate } = this.getTimes(i)
  // if enddate-startdate > workhours, then check if startdate is not older than the day before yesterday
    if (enddate.diff(startdate, 'minutes') > this.tsWorkhours * 60) {
      const dayBeforeYesterday = moment().subtract(2, 'days').startOf('day')
      // Check if either startdate or dayBeforeYesterday falls on a weekend
      if (dayBeforeYesterday.isoWeekday() >= 6) {
        dayBeforeYesterday.subtract(1, 'days'); // substract mutates the object
      }
      if (startdate.isBefore(dayBeforeYesterday)) {
        try {
          const fees = precalcFees_Cache ?? await this.fetchTsFeesBeforeSubmit(i)
          if (precalcFees_Cache === null) precalcFees_Cache = {...fees}
          if (fees.calcot) return true
          else return false
        } catch (error) {
          console.error('Failed getting OT fees for day')
          console.error(error)
          return false
        }
      }
    }

    return false
  }

  /**
   * Displays the calculated fees for the given date index
   * @param dateIndex 
   * @param precalcFees_Cache optional output parameter to get the precalculated fees for the date 
   * to be used outside of this function. This is to cache the result of the DB call
   * null: means that the cache is empty and the function should fill it
   * defined TsCalcInfo: means that the cache is filled and the function should use it
   * undefined: means that the cache should not be used
   */
  private async fillCalcFeesForDateIndex(dateIndex: number, saveParams: HandleTimesheetsParams, precalcFees_Cache?: TsCalcInfo | null): Promise<void> {
    if (!config.DISPLAY_CALC_FEES_BEFORE_SUBMIT_TS) return
    try {
      this.setPrecalculatedFees(dateIndex, true, saveParams)
      const saveParam_longRestHours = saveParams ? JSON.parse(saveParams._data ?? '{}').longresthours : null
      const fees = precalcFees_Cache ?? await this.fetchTsFeesBeforeSubmit(dateIndex, saveParam_longRestHours)
      if (precalcFees_Cache === null) precalcFees_Cache = {...fees}
      this.setPrecalculatedFees(dateIndex, fees, saveParams)
    } catch (error) {
      console.error('Failed getting calculated fees for day index ' + dateIndex)
      console.error(error)
      this.notifSvc.alert(this.t7eNotifLoadCalculateFeesError, this.t7eNotifLoadCalculateFeesErrorTitle)
    } finally {
      this.setPrecalculatedFees(dateIndex, false, saveParams)
    }
  }

  /**
   * Fetches the timesheet fees before submitting for a given date index.
   * @param dateIndex The index of the date for which to fetch the fees.
   * @param saveParam_longRestHours if passed, it will be used instead of the long rest hours from the contract. 
   * This is needed when the user confirms to submit with too many consecutive workdays, so we set the long rest hours to a lower value for the precalculation 
   * @returns The calculated timesheet fees (awaitable).
   */
  private async fetchTsFeesBeforeSubmit(dateIndex: number, saveParam_longRestHours?: number): Promise<TsCalcInfo> {
    const { startdate, enddate } = this.getTimes(dateIndex)
    const c = this.currContract$.value
    const fees = await this.tsSvc.getTsFeesBeforeSubmit({
      contractId: this._ts!.conid!,
      startDate: startdate, endDate: enddate,
      dayOptions:this.getDayOptions(this.form.getRawValue(), dateIndex),
      workingHours: this.tsWorkhours,
      otStep: c?.f_overtimestep || 0.25, 
      longRestHours: saveParam_longRestHours ?? c?.f_saved_longresthours ?? c?.f_longresthours ?? 35,
      shortRestHours: c?.f_shortresthours || 11,
    })
    return fees
  }

  readonly precalculatedFees$: BehaviorSubject<CalculatedFeesForDay_ForPage[]> = new BehaviorSubject<CalculatedFeesForDay_ForPage[]>([])

  private setPrecalculatedFees(dateIndex: number, value: boolean | TsCalcInfo, saveParams: HandleTimesheetsParams): void {
    const arr = this.precalculatedFees$.value
    if (typeof value === 'boolean') arr[dateIndex] = { ...this.precalculatedFees$.value[dateIndex], isLoading: value, contract: this.currContract$.value, saveParams }
    else arr[dateIndex] = { ...value, saveParams, isLoading: false, contract: this.currContract$.value }
    this.precalculatedFees$.next(arr)
  }
  private resetPrecalculatedFees(): void {
    this.precalculatedFees$.next([])
  }

  get isTodayMonday(): boolean {
    return moment().startOf('isoWeek').isSame(moment().startOf('day'))
  }

  submitToDeptHead(): void {
    this.onSubmit(false, TimesheetStatus.atDeptHead)
  }

  approveDeptHead(): void {
    if (!this._ts) return
    this.saveNewStatus(TimesheetStatus.deptHeadApproved)
  }

  approve(): void {
    if (!this._ts) return
    this.saveNewStatus(TimesheetStatus.approved)
  }
  sendBack(): void {
    this.showSaveComment = {
      isCommentOptional: false,
      title: this.t7eSendBackAreYouSureTitle,
      newStatus: TimesheetStatus.atCrewMember,
      saveButtonText: this.t7eSendBackSaveButtonText,
    }
  }
  delete(): void {
    this.showSaveComment = {
      isCommentOptional: true,
      title: this.t7eDeleteAreYouSureTitle,
      newStatus: TimesheetStatus.deleted,
      saveButtonText: this.t7eDeleteSaveButtonText,
    }
  }

  saveCommentReceived(comment: string): void {
    console.log(comment)
    this.saveNewStatus(this.showSaveComment?.newStatus!, comment)
    this.showSaveComment = null
  }

  saveNewStatus(newStatus: TimesheetStatus, savecomment: string | null = null): void {
    if (!this._ts?.tsid) return
    let retVal = this.tsSvc.getSaveNewStatusParams(this._ts,newStatus, savecomment)
    this.save([retVal], this.t7eNotifStatusChangeSuccess, this.t7eNotifStatusChangeError)
  }

  onDateFGChange(index?: number): void {
    this.checkTs(index)
    this.emitSelectedDates()
  }

  private checkTsSub?: Subscription
  isAdjacentCheckDone$ = new BehaviorSubject<boolean>(true) 
  private adjacentCheckDone$: Subject<void>[] = []
  private getAdjacentCheckDone(index: number): Subject<void> {
    if (!this.adjacentCheckDone$[index]) this.adjacentCheckDone$[index] = new Subject<void>()
    return this.adjacentCheckDone$[index]
  }
  checkTs(index?: number): void {
    const aErr: string[] = []
    let indices: number[]
    if (index !== undefined) indices = [index]
    else {
      indices = Array(Object.keys(this.datesFA.controls).length).fill(-1).map((_, i) => i)
    }
    this.checkTsSub?.unsubscribe()

    // sorban egymás után hívjuk meg a checkTs-t, mindig megvárjuk az eredményt,
    // mert a checkTs cache-eli az utolsó számlázott TS-t, és ha egyszerre hívjuk meg, 
    // akkor a cache nem lesz kihasználva
    this.isAdjacentCheckDone$.next(!!this.ts?.tsid || !this.isValid([])) // TODO: majd meg kéne csinálni, hogy ha van tsid, és módosult a dátum, akkor is ellenőrizze a szomszédos ts-eket
    this.checkTsSub = from(indices).pipe(
      concatMap((i) => {
        const params = this.getCheckTimesheetParams(i, aErr)

        if (!this.adjacentTsParams$[i]) this.adjacentTsParams$[i] = new BehaviorSubject<HandleTimesheetsParams | null>(params)
        else this.adjacentTsParams$[i].next(params)
        // wait until child finishes backend call and calls overlapseChecked(...)
        return this.getAdjacentCheckDone(i).pipe(
          take(1),
          timeout(10000),
          catchError(() => of(void 0)),
        )
      }),
      takeUntil(this._destroy$)
    ).subscribe({
        next: () => { },
        error: (err) => { },
        complete: () => { this.isAdjacentCheckDone$.next(true) }
    })
  }

  private getAllSaveParams(dateIndex: number, aErr: string[]): HandleTimesheetsParams | null {
    if (!this.isValid(aErr)) {
      return null
    }
    const formData = this.form.getRawValue()
    // const fromDate = formData.dates[dateIndex].fromDate
    // let startdate = moment(moment(fromDate).format("YYYY-MM-DD") + ' ' + formData.fromTime, 'YYYY-MM-DD HH:mm').utc()
    // let enddate = moment(moment(fromDate).format("YYYY-MM-DD") + ' ' + formData.toTime, 'YYYY-MM-DD HH:mm').utc()
    const { startdate, enddate } = this.getTimes(dateIndex)
    const dayoptions = this.getDayOptions(formData, dateIndex)
    const salaryOverride = (this.ts?.tsid && this.isProdMgr)
      ?  (this.appSvc.isNumber(formData.dates[dateIndex].salaryOverride)
        ? formData.dates[dateIndex].salaryOverride
        : this.appSvc.isNumber(this.ts.salary) // csak akkor töröljük, ha már volt benne érték
          ? NUMERIC_FIELD_TO_DELETE
          : null)
      : null
    const overtimefee = (this.ts?.tsid && this.isProdMgr)
      ? (this.appSvc.isNumber(formData.dates[dateIndex].overtimefeeOverride)
        ? formData.dates[dateIndex].overtimefeeOverride
        : this.appSvc.isNumber(this.ts.overtimefee) // csak akkor töröljük, ha már volt benne érték
          ? NUMERIC_FIELD_TO_DELETE
          : null)
      : null
    const tafeeOverride = (this.ts?.tsid && this.isProdMgr)
      ? (this.appSvc.isNumber(formData.dates[dateIndex].tafeeOverride)
        ? formData.dates[dateIndex].tafeeOverride
        : this.appSvc.isNumber(this.ts.tafee) // csak akkor töröljük, ha már volt benne érték
          ? NUMERIC_FIELD_TO_DELETE
          : null)
      : null
    const taHoursOverride = this.shootingDays$.value[dateIndex]?.taHoursOverride
      ?? (formData.dates[dateIndex].workOnRestDay
        ? (this.currContract$.value?.f_shortresthours ?? this.prodSvc.prodDefaults$.value?.shortresthours)
        : undefined)
    const calcMPWhenFromSf = this.calcMealPenaltyWhenFromSf(dateIndex)
    const comment = [
      formData.dates[dateIndex].message,
      ...this.MEAL_PENALTY === 'timeFromCrewCallFirstValueableOtRate' && calcMPWhenFromSf
        ? [`MP: ${formData.dates[dateIndex].mealPenaltyMealStart}`]
        : []
    ].join('\r\n').trim() || null
    if (this.MEAL_PENALTY === 'timeFromCrewCallFirstValueableOtRate'
      && formData.dates[dateIndex].mealPenaltyMealStart !== null
      && !calcMPWhenFromSf) {
        this.notifSvc.alert(this.t7eMealPenaltyNAMsg, this.t7eMealPenaltyNATitle)
    }
    // const newStatus = this.isProdMgr 
    //     ? TimesheetStatus.approved
    //     : TimesheetStatus.atProdMgr
    let retVal: HandleTimesheetsParams = {
      _tsid: this._ts?.tsid || null,
      //_tsstatus: newStatus,
      _conid: this._ts?.conid,
      _startdate: startdate.local().format('yyyy-MM-DD HH:mm:ss'),
      _enddate: enddate.local().format('yyyy-MM-DD HH:mm:ss'),
      _comment: comment,
      _distandpark: formData.dates[dateIndex].kmFee || 0,
      _distandparkcomment: formData.dates[dateIndex].kmComment,
      _park: formData.dates[dateIndex].parkFee || 0,
      _parkcomment: formData.dates[dateIndex].parkComment,
      _vignette: formData.dates[dateIndex].vignFee || 0,
      _vignettecomment: formData.dates[dateIndex].vignComment,
      _otherfee: formData.dates[dateIndex].otherAllowance || 0,
      _dayoptions: dayoptions.length ? dayoptions : {},
      _salary: salaryOverride,
      _overtimefee: overtimefee,
      _tafee: tafeeOverride,
      _data: JSON.stringify({
        workhours: formData.workhours || null,
        dailybasedrentalrate1: formData.dates[dateIndex].requestRental1 ? this.rentalFee1 : 0,
        dailybasedrentalrate2: formData.dates[dateIndex].requestRental2 ? this.rentalFee2 : 0,
        dailybasedrentalrate3: formData.dates[dateIndex].requestRental3 ? this.rentalFee3 : 0,
        dailybasedrentalrate4: formData.dates[dateIndex].requestRental4 ? this.rentalFee4 : 0,
        dailybasedrentalrate5: formData.dates[dateIndex].requestRental5 ? this.rentalFee5 : 0,
        dailybasedrentalrate6: formData.dates[dateIndex].requestRental6 ? this.rentalFee6 : 0,
        mealPenalty: calcMPWhenFromSf,// A kalkulált meal penalty összeg
        mealPenaltyHours: this.datesFA.controls[dateIndex].get('mealPenaltyHours')?.getRawValue(), // Mennyi időt adott meg meal penalty-ra
        location: formData.dates[dateIndex].location || null,
        shortresthours: taHoursOverride ?? undefined, // ha van megadva a forgatási napra TA óra override, akkor azt küldjük
        longresthours: taHoursOverride ?? undefined, // ha van megadva a forgatási napra TA óra override, akkor azt küldjük
      })
    }

    return retVal
  }

  /** "Kód: Szerződésen felüli" módosítása popupban, OK-ra azonnal menti a TS-t */
  async editCodeOther(dateIndex: number, event?: Event): Promise<void> {
    event?.stopPropagation()
    if (!this._ts?.tsid) return // még nem mentett TS-nél a jóváhagyásnál kérjük be
    const dateFG = this.getDateFG(dateIndex)
    const contract = this.currContract$.value
    let code_other: string
    try {
      code_other = await PromptOtherAllowancePopupComponent.open(this.dialog, {
        code_other: this.codeOther,
        usname: contract?.usname ?? this._ts?.usname ?? null,
        role: contract?.role ?? null,
        startdate: dateFG?.get('fromDate')?.value ?? null,
        otherfee: dateFG?.get('otherAllowance')?.value ?? null,
        currency: this.currency,
        comment: dateFG?.get('message')?.value ?? null,
      })
    } catch {
      return // Mégse: marad a korábbi kód
    }
    this.save([{
      _tsid: this._ts.tsid,
      _data: JSON.stringify({ code_other }),
    }], this.t7eNotifSaveSuccess, this.t7eNotifSaveError)
  }

  private calcMealPenaltyWhenFromSf(dateIndex: number): number  {
    const mealPenaltyRate = this.contractMealPenaltyRate(this.currContract$.value)
    if (!mealPenaltyRate) return 0

    if (this.MEAL_PENALTY === 'timeFromCrewCallFirstValueableOtRate') {
      // get time between crew call and mealPenaltyHoursFromSf
      const tsDateFG = this.getDateFG(dateIndex)
      const mealStartTime = tsDateFG.get('mealPenaltyMealStart')?.getRawValue()
      const crewCallTime = this.shootingDays$.value[dateIndex]?.cc
      if (!crewCallTime || !mealStartTime) return 0
      let mMealStartTime = moment(moment(tsDateFG.get('fromDate')?.getRawValue()).tz(SQL_TIMEZONE).format("YYYY-MM-DD") + ' ' + mealStartTime, 'YYYY-MM-DD HH:mm')
      // add a day if mealPenaltyStartTime is before fromTime
      if (mMealStartTime.isBefore(crewCallTime)) mMealStartTime = mMealStartTime.add(1, 'day')
      const mealPenaltyHours = moment.duration(mMealStartTime.diff(moment(crewCallTime)))
      const dAllowedHours = moment.duration(config.HU.TS.MEAL_PENALTY_START_AFTER_CREW_CALL)
      if (mealPenaltyHours.asMinutes() <= dAllowedHours.asMinutes()) return 0
      return mealPenaltyRate
    }
    
    const mealPenaltyHours = this.datesFA.controls[dateIndex].get('mealPenaltyHours')?.getRawValue()
    if (IS_DENNIS) {
      const gracePeriodHours = this.getGracePeriod(this.currContract$.value) / 60
      if (gracePeriodHours >= mealPenaltyHours) return 0
    }
    return mealPenaltyRate * mealPenaltyHours
  }

  private getGracePeriod(c: ContractParsed | null): number {
    return +(c?.f_saved_graceperiod || c?.f_graceperiod || 0)
  }

  private getDayOptions(formData: any, dateIndex: number): DayOptions[] {
    const dayoptions = []
    if (formData.dates[dateIndex].monthlyRentsDue) dayoptions.push(DayOptions.MonthlyRentsDue)
    if (formData.dates[dateIndex].halfDay) dayoptions.push(DayOptions.HalfDay)
    if (formData.dates[dateIndex].travelDay) dayoptions.push(DayOptions.TravelDay)
    if (formData.dates[dateIndex].perdiem) dayoptions.push(DayOptions.PerDiem)
    if (formData.dates[dateIndex].mealPenaltyAsDayOptions) dayoptions.push(DayOptions.MealPenalty)
    if (formData.dates[dateIndex].restDay) dayoptions.push(DayOptions.RestDay)
    return dayoptions
  }

  private getTimes(dateIndex: number): { startdate: moment.Moment, enddate: moment.Moment } {
    const formData = this.form.value
    const fromDate = formData.dates[dateIndex].fromDate
    let startdate = moment(moment(fromDate).format("YYYY-MM-DD") + ' ' + formData.fromTime, 'YYYY-MM-DD HH:mm').utc()
    let enddate = moment(moment(fromDate).format("YYYY-MM-DD") + ' ' + formData.toTime, 'YYYY-MM-DD HH:mm').utc()
    if (moment(formData.toTime, 'HH:mm').isBefore(moment(formData.fromTime, 'HH:mm'))) enddate = moment(enddate).add(1, 'day')
    return { startdate, enddate }
  }
  
  getCheckTimesheetParams(dateIndex: number, aErr: string[]): CheckTimesheetParams | null {
    if (!this.isValid(aErr)) {
      return null
    }
    const formData = this.form.value
    let startdate = moment(moment(formData.dates[dateIndex].fromDate).format("YYYY-MM-DD") + ' ' + formData.fromTime, 'YYYY-MM-DD HH:mm').utc()
    let enddate = moment(moment(formData.dates[dateIndex].fromDate).format("YYYY-MM-DD") + ' ' + formData.toTime, 'YYYY-MM-DD HH:mm').utc()
    if (moment(formData.toTime, 'HH:mm').isBefore(moment(formData.fromTime, 'HH:mm'))) enddate = moment(enddate).add(1, 'day')
    // const dayoptions = []
    // if (formData.halfDay) dayoptions.push(DayOptions.HalfDay)
    // if (formData.travelDay) dayoptions.push(DayOptions.TravelDay)
    // if (formData.perdiem) dayoptions.push(DayOptions.PerDiem)
    let retVal: HandleTimesheetsParams = {
      _tsid: this._ts?.tsid || null,
      _conid: this._ts?.conid,
      _startdate: this.parser.datetimeForSql(startdate),
      _enddate: this.parser.datetimeForSql(enddate) ,
    }

    return retVal
  }

  overlapseChecked(checkTsResult: CheckTs, dateIndex: number): void {
    console.log(checkTsResult);
    if (!this.aOverlapse$[dateIndex]) {
      this.aOverlapse$[dateIndex] = new BehaviorSubject<CheckTs | null>(checkTsResult)
      this.aOverlapseSubscriptioins[dateIndex] = this.aOverlapse$[dateIndex].subscribe(checkTsResult => {
        this.warnHolidayIfNeeded(checkTsResult, dateIndex)
        if (checkTsResult?.isAleadyInvoicedPeriod) {
          this.warnAlreadyInvoicedPeriod(checkTsResult, dateIndex)
        }
      })
    }
    else this.aOverlapse$[dateIndex].next(checkTsResult)

    // ACK: child backend call completed for this index
    this.getAdjacentCheckDone(dateIndex).next()
  }

  private warnHolidayIfNeeded(checkTsResult: CheckTs | null, dateIndex: number): void {
    if (checkTsResult?.daytype === 'holy') {
      const date = moment(this.form.value.dates[dateIndex].fromDate).format(SQL_DATE_FORMAT)
      const msg = `${date} ${this.t7eNotifHolidayWarning}`
      this.notifSvc.confirm({
        message: msg,
        title: this.t7eNotifHolidayWarningTitle,
        cancelButtonText: this.t7eIAmSureButtonText,
        okButtonText: this.t7eDeleteSaveButtonText,
        okCallback: () => {
          this.removeDate(dateIndex)
        },
      })
    }
  }

  private warnAlreadyInvoicedPeriod(checkTsResult: CheckTs | null, dateIndex: number): boolean {
    const tsDate = moment(this.form.value.dates[dateIndex].fromDate)
    const lastInvoicedDate = checkTsResult?.isAleadyInvoicedPeriod
    if (lastInvoicedDate) {
      const msg = `Timesheet ${tsDate.format(SQL_DATE_FORMAT)}: ${this.t7eNotifAlreadyInvoicedWarning} ${lastInvoicedDate.format(SQL_DATE_FORMAT)}`
      this.notifSvc.alert(msg, this.t7eNotifAlreadyInvoicedWarningTitle)
      return true
    }
    return false
  }

  private isValid(aErr: string[]): boolean {
    if (!this._ts?.conid) {
      aErr.push('Nincs kiválasztva szerepkör.')
    }
    // push to aErr, if invalid fromTime, toTime, workhours
    const formData = this.form.getRawValue()
    if (!formData.fromTime) aErr.push(this.t7eNotifCannotSaveNoFromTime)
    if (!formData.toTime) aErr.push(this.t7eNotifCannotSaveNoToTime)
    if (!formData.workhours) aErr.push(this.t7eNotifCannotSaveNoWorkhours)
    if (formData.workhours && formData.workhours < 0) aErr.push(this.t7eNotifCannotSaveNegativeWorkhours)
    if (formData.workhours && formData.workhours > 24) aErr.push(this.t7eNotifCannotSaveTooManyWorkhours)
    // is fromTime and endTime in hh:mm format?
    if (formData.fromTime && !moment(formData.fromTime, 'HH:mm').isValid()) aErr.push(this.t7eNotifCannotSaveInvalidFromTime)
    if (formData.toTime && !moment(formData.toTime, 'HH:mm').isValid()) aErr.push(this.t7eNotifCannotSaveInvalidToTime)
    // if diff between fromTime and toTime is less than 4 hour, push to aErr
    const { startdate, enddate } = this.getTimes(0)
    const diffInHours = enddate.diff(startdate, 'hours', true)
    if (diffInHours < 4 || diffInHours > 20) aErr.push(this.t7eNotifCannotSaveTooFewHours)
    
    if (formData.dates
      .some((date: any) => date.mealPenaltyMealStart && !moment(date.mealPenaltyMealStart, 'HH:mm').isValid())) {
      aErr.push(this.t7eNotifCannotSaveInvalidMealPenaltyMealStart)
    }

    return !aErr.length
  }

  private getFG(formData: TimesheetParsed | null): FormGroup {
    if (this.AUTOFILL_TIMES) formData = this.setDatesIfNew(formData)
    const fillDate = this.AUTOFILL_TIMES || !!formData?.tsid
    return this.fb.group({
      dates: this.fb.array([
       this.getDatesFG(formData)
      ]),
      workhours: [formData?.workhours || 12, [NumericValidator]],
      fromTime: [fillDate ? moment(formData?.dStartDate).format('HH:mm') : null, Validators.required],
      toTime: [fillDate ? moment(formData?.dEndDate).format('HH:mm') : null, Validators.required],
    })
  }

  private getDatesFG(formData: TimesheetParsed | null): FormGroup {
    const isNew = !formData?.tsid
    const c = this.currContract$?.value  
    return this.fb.group({
      fromDate: [formData?.dStartDate || null, Validators.required],
      message: [formData?.comment],
      location: [],
      kmFee: [formData?.distandpark, [NumericValidator]],
      kmComment: [formData?.distandparkcomment],
      parkFee: [formData?.park, [NumericValidator]],
      parkComment: [formData?.parkcomment],
      vignFee: [formData?.vignette, [NumericValidator]],
      vignComment: [formData?.vignettecomment],
      requestRental1: [isNew ? this.contractSvc.isDefaultChecked(c?.f_dailybasedrental1type) : formData?.f_dailybasedrentalrate1],
      requestRental2: [isNew ? this.contractSvc.isDefaultChecked(c?.f_dailybasedrental2type) : formData?.f_dailybasedrentalrate2],
      requestRental3: [isNew ? this.contractSvc.isDefaultChecked(c?.f_dailybasedrental3type) : formData?.f_dailybasedrentalrate3],
      requestRental4: [isNew ? this.contractSvc.isDefaultChecked(c?.f_dailybasedrental4type) : formData?.f_dailybasedrentalrate4],
      requestRental5: [isNew ? this.contractSvc.isDefaultChecked(c?.f_dailybasedrental5type) : formData?.f_dailybasedrentalrate5],
      requestRental6: [isNew ? this.contractSvc.isDefaultChecked(c?.f_dailybasedrental6type) : formData?.f_dailybasedrentalrate6],

      monthlyRentsDue: [this.isMonthlyRentsDue(formData?.dayoptions)],
      workOnRestDay: [(formData?.longresthours == this.prodSvc.prodDefaults$.value?.shortresthours)],
      halfDay: [this.isHalfDay(formData?.dayoptions)],
      travelDay: [this.isTravelDay(formData?.dayoptions)],
      perdiem: [this.isPerdiem(formData?.dayoptions)],
      restDay: [this.isRestDay(formData?.dayoptions)],
      mealPenalty: [this.isMealPenalty(formData?.dayoptions)],

      // Ha Meal Penalty == firstValueableOtRate
      mealPenaltyAsDayOptions: [this.isMealPenalty(formData?.dayoptions)],

      // Ha MealPenalty == setAtProdDeptSf
      mealPenaltyHours: [formData?.f_mealPenaltyHours || 0],
      mealPenaltyFee: [formData?.f_mealPenalty || 0],

      // Ha MealPenalty == timeFromCrewCallFirstValueableOtRate
      mealPenaltyMealStart: null,

      otherAllowance: [formData?.otherfee, [NumericValidator]],
      salaryOverride: [formData?.salary, [NumericValidator]],
      overtimefeeOverride: [formData?.overtimefee, [NumericValidator]],
      tafeeOverride: [formData?.tafee, [NumericValidator]],
    })
  }

  setDates(dates: Date[] | null): void {
    if (!dates?.length) return
    this.datesFA.clear()
    dates
      .sort((a, b) => moment(a).isBefore(moment(b)) ? -1 : 1)
      .forEach((date, index) => {
        this.datesFA.push(this.getDatesFG({ dStartDate: date }))
        // a checkTs sajnos kell, mert ha az app-day-selectorban változtat a user dátumot, akkor nem fut le a checkTs
        // viszont így meg más esetekben kétszer fut le
        this.checkTs(index)
    })
    this.resetPrecalculatedFees()
  }

  addDate(): void {
    const prevLastIndex = this.datesFA.controls.length - 1
    const prevDate = this.datesFA.controls[prevLastIndex].get('fromDate')?.value
    const dayJump = this.getDayJump()
    if (dayJump === null) {
      // TODO többnyelvűsíteni kell
      this.notifSvc.error('Nincs több nap.')
      return
    }
    const newDate = moment(prevDate).add(dayJump, 'day').toDate()
    this.datesFA.push(this.getDatesFG({ dStartDate: newDate }))
    this.checkTs(prevLastIndex + 1)
    this.emitSelectedDates()
  }
  removeDate(index: number): void {
    this.datesFA.removeAt(index)
    this.removeAdjacentCheck(index)
    this.emitSelectedDates()
  }
  removeAdjacentCheck(index: number): void {
    this.adjacentTsParams$[index]?.complete()
    this.adjacentTsParams$.splice(index, 1)
    this.aOverlapse$[index]?.complete()
    this.aOverlapse$.splice(index, 1)
    this.aOverlapseSubscriptioins[index]?.unsubscribe()

    this.adjacentCheckDone$[index]?.complete()
    this.adjacentCheckDone$.splice(index, 1)
  }

  private getDayJump(): number | null {
    const prevLastIndex = this.datesFA.controls.length - 1
    const prevDate = this.datesFA.controls[prevLastIndex].get('fromDate')?.value
    let dayJump = 1
    let dNextDay = moment(prevDate).add(dayJump, 'day').toDate()
    const getAlreadyExists = (dNextDay: Date) => this.datesFA.controls.some(ctrl => moment(ctrl.get('fromDate')?.value).isSame(dNextDay, 'day'))
    // van már `tomorrow` a datesFA-ban? 
    // A `tomorrow` nem a jövőben van?
    // A `tomorrow` nem a produkció vége után van?
    let alreadyExists = getAlreadyExists(dNextDay)
    if (!alreadyExists && dNextDay < new Date() && dNextDay <= this.prodSvc.prodDefaults$.value?.dShootingend!) return dayJump
    
    dayJump = -1
    dNextDay = moment(prevDate).add(dayJump, 'day').toDate()
    alreadyExists = getAlreadyExists(dNextDay)
    //  `tomorrow` nem a produkció
    if (!alreadyExists && dNextDay >= this.minDate$.value!) return dayJump

    // find next available day that doesn't already exist and is before the production end date
    dayJump = 1
    let direction = +1
    dNextDay = moment(prevDate).add(dayJump, 'day').toDate()
    while (getAlreadyExists(dNextDay) || dNextDay > new Date() || dNextDay < this.minDate$.value!) {
      dayJump += direction
      dNextDay = moment(prevDate).add(dayJump, 'day').toDate()
      if (dNextDay > this.prodSvc.prodDefaults$.value?.dShootingend! || dNextDay > new Date()) {
        // reset and change direction
        direction = -1
        dayJump = -1
        dNextDay = moment(prevDate).add(dayJump, 'day').toDate()
      }
      if (dNextDay < this.minDate$.value!) {
        return null
      }
    }
    return dayJump
  }

  private emitSelectedDates(): void {
    const dates = this.datesFA.value
      .map((x: any) => x.fromDate instanceof Date ? x.fromDate : x.fromDate.toDate())
    this.selectedDateChange.emit(dates)
  }

  showHistory(e: MouseEvent, propcodeOrColname: string, isFlex: boolean) {
    const tsid = this._ts?.tsid
    //console.log('Előzmények a ts-hez: ', tsid);
    if (!tsid) return
    this.history =
    {
      tableid: FlexTables.timesheets,
      id: tsid,
      propcode: isFlex ? propcodeOrColname : undefined,
      colname: isFlex ? undefined : propcodeOrColname,
    }
  }

  isMonthlyRentsDue(dayOptions: DayOptions[] | DayOptions | null | undefined): boolean {
    if (!dayOptions) return false
    if (Array.isArray(dayOptions)) {
      return dayOptions.some(x => x == DayOptions.MonthlyRentsDue)
    }
    return dayOptions == DayOptions.MonthlyRentsDue
  }

  isHalfDay(dayOptions: DayOptions[] | DayOptions | null | undefined): boolean {
    if (!dayOptions) return false
    if (Array.isArray(dayOptions)) {
      return dayOptions.some(x => x == DayOptions.HalfDay)
    }
    return dayOptions == DayOptions.HalfDay
  }

  isTravelDay(dayOptions: DayOptions[] | DayOptions | null | undefined): boolean {
    if (!dayOptions) return false
    if (Array.isArray(dayOptions)) {
      return dayOptions.some(x => x == DayOptions.TravelDay)
    }
    return dayOptions == DayOptions.TravelDay
  }
  isPerdiem(dayOptions: DayOptions[] | DayOptions | null | undefined) {
    if (Array.isArray(dayOptions)) {
      return dayOptions.some(x => x == DayOptions.PerDiem)
    }
    return dayOptions == DayOptions.PerDiem
  }
  isRestDay(dayOptions: DayOptions[] | DayOptions | null | undefined) {
    if (Array.isArray(dayOptions)) {
      return dayOptions.some(x => x == DayOptions.RestDay)
    }
    return dayOptions == DayOptions.RestDay
  }
  isMealPenalty(dayOptions: DayOptions[] | DayOptions | null | undefined) {
    if (Array.isArray(dayOptions)) {
      return dayOptions.some(x => x == DayOptions.MealPenalty)
    }
    return dayOptions == DayOptions.MealPenalty
  }
  isMealPenaltyItemSelected(dateIndex: number, item: number): boolean {
    const selectedMealPenaltyHours = this.datesFA.controls[dateIndex].get('mealPenaltyHours')?.value
    return selectedMealPenaltyHours === item
  }

  warnSalaryOverride(triggerField: 'halfDay' | 'travelDay' | 'restDay'): void {
    if (!this.canEdit(this._ts) || !this._ts?.tsid) return
    const salaryOverride = this.datesFA.getRawValue()?.[0]?.salaryOverride
    if (!salaryOverride) return
    const warnSalaries = this.warnSalaryOverride$.value
    if (!warnSalaries.includes(triggerField)) {
      this.warnSalaryOverride$.next([...warnSalaries, triggerField])
    }
  }

  isDateTooOld(date: Date): boolean{
    // console.table({
    //   date: moment(date).format(SQL_DATETIME_FORMAT),
    //   dateTz: moment(date).tz(SQL_TIMEZONE).format(SQL_DATETIME_FORMAT),
    //   contractStartDate: moment(this.currContract$.value?.dStartdate).format(SQL_DATETIME_FORMAT),
    //   contractStartDateTz: moment(this.currContract$.value?.dStartdate).tz(SQL_TIMEZONE).format(SQL_DATETIME_FORMAT),
    // })
    // console.log('RetVal:', (moment(date).tz(SQL_TIMEZONE).isBefore(moment(this.currContract$.value?.dStartdate).tz(SQL_TIMEZONE))))
    return (moment(date).tz(SQL_TIMEZONE).isBefore(moment(this.currContract$.value?.dStartdate).tz(SQL_TIMEZONE)))
  }

  getDateForSectionTitle(date: Date): string {
    return moment(date).locale(this.lang).format('yyyy-MM-DD dddd')
  }
  trackByControlname(index: number, item: { controlName: string }): string {
    return item.controlName
  }
  trackByLocId = (index: number, item: any) => item.locId
  trackByDate = (index: number, item: any) => item.value?.fromDate
  private scrollFormIntoView(): void {
    setTimeout(() => {
      if (this.layout === 'grid-form' || this._ts?.tsid) return
      document.getElementById('workhours-and-times')?.scrollIntoView({behavior: 'smooth', block: "start", inline: "nearest"})
    }, 0);
  }


  /** T7E */
  // page title
  t7ePageTitle = this.t7e.getTranslation('app-edit-timesheet', 'page-title', 'title', 'Timesheet szerkesztése')
  t7eProdmgrMsg = this.t7e.getTranslation('app-edit-timesheet', 'page-warning', 'title', 'A gyártásvezető üzenete')
  t7eHourSuffix = this.t7e.getTranslation('form', 'hour-suffix', 'innerText', null)

  // Btn texts and titles
  t7eBtnDeleteAlreadyDeletedTitle = this.t7e.getTranslation('app-edit-timesheet', 'btn-delete-already-deleted', 'title', 'Már törölve')
  t7eBtnOpenPrevTsTitle = this.t7e.getTranslation('app-edit-timesheet', 'btn-open-prev-ts', 'title', 'Megelőző bevitt nap TS megnyitása felugró ablakban')
  t7eBtnSubmitNoContractTitle = this.t7e.getTranslation('app-edit-timesheet', 'can-submit-no--contract', 'title', 'Válassz szerepkört!')
  t7eBtnSubmitFormInvalidTitle = this.t7e.getTranslation('app-edit-timesheet', 'can-submit-form-invalid', 'title', 'Az űrlap nincs helyesen kitöltve')
  t7eBtnSubmitFormNotSavedTitle = this.t7e.getTranslation('app-edit-timesheet', 'can-submit-form-not-saved', 'title', 'Előbb mentsd el a változtatásokat')
  t7eBtnSubmitSavingTitle = this.t7e.getTranslation('app-edit-timesheet', 'can-submit-saving', 'title', 'Mentés folyamatban')
  t7eBtnSubmitAdjacentCheckNotDoneTitle = this.t7e.getTranslation('app-edit-timesheet', 'can-submit-adjacent-check-not-done', 'title', null, null, 'Timesheet ellenőrzés folyamatban')
  t7eBtnSubmitOverlapTitle = this.t7e.getTranslation('app-edit-timesheet', 'can-submit-overlap', 'title', 'Átfedés egy másik timesheettel')
  t7eBtnSubmitInvAcceptedTitle = this.t7e.getTranslation('app-edit-timesheet', 'can-submit-inv-accepted', 'title', 'Számla már befogadva')
  t7eBtnSubmitAttGeneratingTitle = this.t7e.getTranslation('app-edit-timesheet', 'can-submit-att-generating', 'title', 'Számlamelléklet épp generálás alatt van')
  t7eBtnSubmitAlreadySubmittedTitle = this.t7e.getTranslation('app-edit-timesheet', 'can-submit-already-submitted', 'title', 'Már beküldve')
  t7eBtnSubmitFutureTitle = this.t7e.getTranslation('app-edit-timesheet', 'can-submit-future', 'title', 'Jövőbeli timesheet nem küldhető be')
  t7eBtnSubmitNoPermissionTitle = this.t7e.getTranslation('app-edit-timesheet', 'can-submit-no-permission', 'title', 'Nincs jogosultságod')
  t7eBtnSubmitNotWaitingApprovalTitle = this.t7e.getTranslation('app-edit-timesheet', 'can-submit-not-waiting-approval', 'title', 'Nem vár jóváhagyásra')
  t7eBtnSaveDebugTitle = this.t7e.getTranslation('app-edit-timesheet', 'can-save-debug', 'title', 'DEBUG bekapcsolva')
  t7eBtnSaveSavingTitle = this.t7e.getTranslation('app-edit-timesheet', 'can-save-saving', 'title', 'Mentés folyamatban...')
  t7eBtnSaveNoChangeTitle = this.t7e.getTranslation('app-edit-timesheet', 'can-save-no-change', 'title', 'Nincs változtatás az űrlapon')
  t7eBtnSaveFormInvalidTitle = this.t7e.getTranslation('app-edit-timesheet', 'can-save-form-invalid', 'title', 'Kitöltési hibák')
  t7eBtnSaveTitle = this.t7e.getTranslation('app-edit-timesheet', 'can-save', 'title', 'Mentés')
  t7eBtnDisabledTsNotEditableTitle = this.t7e.getTranslation('app-edit-timesheet', 'btn-disabled-ts-not-editable', 'title', 'Letiltott timesheet nem szerkeszthető')
  t7eDisabledTsNotEditableDescr = this.t7e.getTranslation('app-edit-timesheet', 'btn-disabled-ts-not-editable', 'descr', 'Kérd a gyártásvezetőt, hogy hagyja jóvá vagy engedélyezze a szerkesztést')

  // notif-msgs
  t7eNotifUnknownTs = this.t7e.getTranslation('app-edit-timesheet', 'notif-msg', 'unknown-ts', 'Ismeretlen Timesheet')
  t7eNotifMultipleTsFromRouteError = this.t7e.getTranslation('app-edit-timesheet', 'notif-msg', 'multiple-ts-from-route', 'Timesheet betöltés hiba')
  t7eNotifTsLoadError = this.t7e.getTranslation('app-edit-timesheet', 'notif-msg', 'ts-load-error', 'Hiba történt a Timesheet betöltése közben')
  t7eNotifSubmitting = this.t7e.getTranslation('app-edit-timesheet', 'notif-msg', 'submitting', 'Küldés...')
  t7eNotifDbError = this.t7e.getTranslation('app-edit-timesheet', 'notif-msg', 'db-error', 'Adatbázis hiba. Talán nem sikerült a mentés')
  t7eNotifCannotSaveError = this.t7e.getTranslation('app-edit-timesheet', 'notif-msg', 'cannot-save-error', 'Nem menthető: ')
  t7eNotifSaveSuccess = this.t7e.getTranslation('app-edit-timesheet', 'notif-msg', 'save-success', 'A mentés sikerült')
  t7eNotifSaveError = this.t7e.getTranslation('app-edit-timesheet', 'notif-msg', 'save-error', 'Sikertelen mentés')
  t7eNotifNthTsCannotSaveError = this.t7e.getTranslation('app-edit-timesheet', 'notif-msg', 'nth-ts-cannot-save-error', '. timesheet nem menthető: ')
  t7eNotifSubmitSuccess = this.t7e.getTranslation('app-edit-timesheet', 'notif-msg', 'submit-success', 'Timesheet beküldve.')
  t7eNotifSaveErrorColon = this.t7e.getTranslation('app-edit-timesheet', 'notif-msg', 'save-error-colon', 'Sikertelen mentés: ')
  t7eNotifStatusChangeSuccess = this.t7e.getTranslation('app-edit-timesheet', 'notif-msg', 'status-change-success', 'Státusz módosítás sikerült')
  t7eNotifStatusChangeError = this.t7e.getTranslation('app-edit-timesheet', 'notif-msg', 'status-change-error', 'Sikertelen státusz módosítás')
  t7eNotifCannotSaveNoContract = this.t7e.getTranslation('app-edit-timesheet', 'notif-msg', 'cannot-save-no-contract', 'Nincs kiválasztva szerepkör.')
  t7eNotifCannotSaveNoFromTime = this.t7e.getTranslation('app-edit-timesheet', 'notif-msg', 'cannot-save-no-from-time', 'Nincs megadva kezdési idő.')
  t7eNotifCannotSaveNoToTime = this.t7e.getTranslation('app-edit-timesheet', 'notif-msg', 'cannot-save-no-to-time', 'Nincs megadva befejezési idő.')
  t7eNotifCannotSaveNoWorkhours = this.t7e.getTranslation('app-edit-timesheet', 'notif-msg', 'cannot-save-no-workhours', 'Nincs megadva munkaóra.')
  t7eNotifCannotSaveNegativeWorkhours = this.t7e.getTranslation('app-edit-timesheet', 'notif-msg', 'cannot-save-negative-workhours', 'A munkaóra nem lehet negatív.')
  t7eNotifCannotSaveTooManyWorkhours = this.t7e.getTranslation('app-edit-timesheet', 'notif-msg', 'cannot-save-too-many-workhours', 'A munkaóra nem lehet több, mint 24 óra.')
  t7eNotifCannotSaveInvalidFromTime = this.t7e.getTranslation('app-edit-timesheet', 'notif-msg', 'cannot-save-invalid-from-time', 'A kezdési idő nem megfelelő formátumú.')
  t7eNotifCannotSaveInvalidToTime = this.t7e.getTranslation('app-edit-timesheet', 'notif-msg', 'cannot-save-invalid-to-time', 'A befejezési idő nem megfelelő formátumú.')
  t7eNotifCannotSaveInvalidMealPenaltyMealStart = this.t7e.getTranslation('app-edit-timesheet', 'notif-msg', 'cannot-save-invalid-meal-penalty-meal-start', 'Az ebéd kezdő időpontja nem megfelelő formátumú.')
  t7eMealPenaltyNAMsg = this.t7e.getTranslation('app-edit-timesheet', 'notif-msg', 'meal-penalty-na-msg', null, null, 'Nem késett az ebéd, vagy nincs megadva összeg a start formban. Az igény nem lesz elmentve.')
  t7eMealPenaltyNATitle = this.t7e.getTranslation('app-edit-timesheet', 'notif-msg', 'meal-penalty-na-title', null, null, 'Nem jár meal penalty')
  t7eNotifCannotSaveTooFewHours = this.t7e.getTranslation('app-edit-timesheet', 'notif-msg', 'cannot-save-too-few-hours', 'Minimum 4 órát, maximum 20 órát lehet megadni.')
  t7eNotifHolidayWarning = this.t7e.getTranslation('app-edit-timesheet', 'notif-msg', 'holiday-warning', 'ünnepnap. Biztos, hogy dolgoztál?')
  t7eNotifHolidayWarningTitle = this.t7e.getTranslation('app-edit-timesheet', 'notif-msg', 'holiday-warning-title', 'Ünnepnap')
  t7eIAmSureButtonText = this.t7e.getTranslation('app-edit-timesheet', 'notif-msg', 'i-am-sure-button-text', 'Biztos')
  t7eNotifAlreadyInvoicedWarning = this.t7e.getTranslation('app-edit-timesheet', 'notif-msg', 'already-invoiced-warning', null, null, 'Olyan napra próbáltál timesheetet beküldeni, amelyre már készült számla. Lépj kapcsolatba a produkcióval!')
  t7eNotifAlreadyInvoicedWarningTitle = this.t7e.getTranslation('app-edit-timesheet', 'notif-msg', 'already-invoiced-warning-title', null, null, 'Leszámlázott időszak')
  t7eLocationConfirmEligibleText = this.t7e.getTranslation('app-edit-timesheet', 'location-confirm-eligible', 'text', null, null, 'Csak akkor jelöld be, ha SAJÁT AUTÓVAL mentél, és jár üzemanyag-térítés')
  t7eNotifChangingWorkhoursHasNoEffect = this.t7e.getTranslation('app-edit-timesheet', 'notif-msg', 'changing-workhours-has-no-effect', null, null, 'Jóváhagyott time sheeten a munkaóra megváltoztatása nem befolyásolja a túlóradíjat')
  t7eNotifChangingWorkhoursHasNoEffectTitle = this.t7e.getTranslation('app-edit-timesheet', 'notif-msg', 'changing-workhours-has-no-effect-title', null, null, 'A változtatás hatástalan')
  t7eNotifLoadCalculateFeesError = this.t7e.getTranslation('app-edit-timesheet', 'notif-msg', 'load-calculate-fees-error', null, null, 'Hiba történt a díjak kalkulációja közben')
  t7eNotifLoadCalculateFeesErrorTitle = this.t7e.getTranslation('app-edit-timesheet', 'notif-msg', 'load-calculate-fees-error-title', null, null, 'Díjkalkuláció hiba')
  // sendBack, delete
  t7eSendBackAreYouSureTitle = this.t7e.getTranslation('app-edit-timesheet', 'send-back', 'are-you-sure', 'Valóban visszaküldöd? Miért?')
  t7eSendBackSaveButtonText = this.t7e.getTranslation('app-edit-timesheet', 'send-back', 'save-button-text', 'Visszaküldés')
  t7eDeleteAreYouSureTitle = this.t7e.getTranslation('app-edit-timesheet', 'delete', 'are-you-sure', 'Biztos törlöd?')
  t7eDeleteSaveButtonText = this.t7e.getTranslation('app-edit-timesheet', 'delete', 'save-button-text', 'Törlés')

  t7eCheckAgeTooOldTitle = this.t7e.getTranslation('app-edit-timesheet', 'check-age', 'too-old-title', null, null, 'Túl régi timesheet')
  t7eCheckAgeTooOld = this.t7e.getTranslation('app-edit-timesheet', 'check-age', 'too-old', null, null, 'Egy hétnél régebbi timesheet nem küldhető be. Fordulj a gyártásvezetőhöz egyedi elbíráslásért')
  t7eCheckAgeTooOldNoOt = this.t7e.getTranslation('app-edit-timesheet', 'check-age', 'too-old-no-ot',null,null, 'Egy hétnél régebbi timesheet nem küldhető be. Fordulj a gyártásvezetőhöz egyedi elbíráslásért')
  t7eSubmitAsDisabled = this.t7e.getTranslation('app-edit-timesheet', 'check-age', 'submit-as-disabled', null, null, 'Mentés így, egyeztetek a gyártásvezetővel')
  t7eModifyTimes = this.t7e.getTranslation('app-edit-timesheet', 'check-age', 'modify-times', null, null, 'Időpontok módosítása')

  getAllDirtyFields(): string[] {
    const dirtyFields: string[] = []
    // Find dirty fields recursively
    const findDirtyFields = (control: AbstractControl, path: string) => {
      if (control.dirty) {
        dirtyFields.push(path)
      }
      if (control instanceof FormGroup) {
        Object.keys(control.controls).forEach(key => {
          findDirtyFields(control.controls[key], path ? `${path}.${key}` : key)
        })
      } else if (control instanceof FormArray) {
        control.controls.forEach((ctrl, index) => {
          findDirtyFields(ctrl, `${path}[${index}]`)
        })
      }
    };

    findDirtyFields(this.form, '');
    return dirtyFields;
  }
}

export type ShootingDay_ForPage = {
  cc: moment.Moment | null,
  cw: moment.Moment | null,
  taHoursOverride: number | null,
}

export type CalculatedFeesForDay_ForPage = PrecalcPopupData
